import {Duration} from 'luxon'
import {omit, partition} from 'remeda'
import {Prettify} from './type'

// export const delay = (ms: number) => new Promise(_ => setTimeout(_, ms))
export const delay = (ms: number, signal?: AbortSignal) =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(resolve, ms)
    if (signal)
      signal.addEventListener('abort', () => {
        clearTimeout(timeoutId)
        reject(new DOMException('The operation was aborted.', 'AbortError'))
      })
  })

export const doFn = <T, R>(f: () => R) => f()

/** run a function and return the result. Simple AF, but good if you want a single expression */
export const doTo = <T, R>(o: T, f: (o: T) => R) => f(o)

// run a function if the result of the parameter is not null
export const doSafe = <T, R>(o: T | null | undefined, f: (o: T) => R) => (o == null ? undefined : f(o))

export type OK<T> = T & {ok: true}
export type OKNarrow<T> = T extends {ok: true} ? T : never
export type ErrNarrow<T> = T extends {ok: false} ? T : never
export type Err<E = {message: string}> = E & {ok: false}
export type Res<T, E> = OK<T> | Err<E>

export function ok(): OK<{}>
export function ok<T>(value: T): T extends object ? OK<T> : OK<{value: T}>
export function ok<T>(value?: T): any {
  if (value == null) return {ok: true}
  if (typeof value == 'object' && value !== null) {
    if (value.constructor === Object) return {ok: true, ...value}
    Object.defineProperty(value, 'ok', {value: true, enumerable: true})
    return value
  }
  return {ok: true, value}
}

export function err(): Err<{}>
export function err<E extends object | string>(error: E): E extends object ? Err<E> : Err<{message: E}>
export function err<E>(error?: E): any {
  if (error == null) return {ok: false, toString: () => 'error (no message given)'}
  if (typeof error === 'object' && error !== null) {
    if (error.constructor === Object)
      return {ok: false, ...error, toString: () => ('message' in error ? error.message : JSON.stringify(error))}
    Object.defineProperty(error, 'ok', {value: false, enumerable: true})
    return error
  }
  if (typeof error === 'string') return {ok: false, message: error, toString: () => error}
  return {ok: false, message: (error as any).toString(), toString: (error as any).toString}
}

/** Helper to detect if a type is 'any' */
type IsAny<T> = 0 extends 1 & T ? true : false

export const isErr = <T>(v: T): v is Extract<T, {ok: false}> =>
  typeof v == 'object' && v != null && 'ok' in v && v.ok == false
export const isOk = <T>(v: T): v is Extract<T, {ok: true}> =>
  typeof v == 'object' && v != null && 'ok' in v && v.ok == true

/** Turns exceptions into OK/Err  if needed. If the function already returns an OK/Err we don't wrap it */
// type ResultType<T, E extends object = Error> =

/** Catches any exception in the given function or promise and returns an OK/Err.
 * Resolving the promise: If it is a function we call it and wait for the promise to resolve, or if it a promise we wait to resolve it.
 * How the results are handled:
 *  If the existing result is an object with {ok: true} or {ok: false}, it is returned as is.
 *  If it is a primitive, we will return it as {ok: true, value: <primitive>}
 *  If it is an object, we will return it with ok added {ok: true, ...<object>}
 *  If we catch an exception we will ad ok = false to it object like, otherwise we will return {ok:false, message: <exception>}
 * @example
 * okOrCatch(mightThrow('https://api.example.com/data'))
 */
export const okOrCatch = async <T>(
  promise: Promise<T>,
  props?: {
    caughtErrorMessagePrefix?: string
  }
): Promise<
  IsAny<T> extends true ? OK<{}> | Err<Error>
  : T extends {ok: boolean} ? T | Err<Error>
  : T extends void ? OK<{}> | Err<Error>
  : T extends object ? OK<T> | Err<Error>
  : OK<{value: T}> | Err<Error>
> => {
  try {
    const result = await promise
    if (isOk(result) || isErr(result)) return result as any // Already an OK/Err like structure
    return ok(result) as any
  } catch (e: any) {
    const errorMessage = e.message ?? e.toString()
    return err(props?.caughtErrorMessagePrefix ? `${props.caughtErrorMessagePrefix}: ${errorMessage}` : errorMessage)
  }
}

/** Catches any exception in the given synchronous function and returns an OK/Err.
 * How the results are handled:
 *  If the existing result is an object with {ok: true} or {ok: false}, it is returned as is.
 *  If it is a primitive, we will return it as {ok: true, value: <primitive>}
 *  If it is an object, we will return it with ok added {ok: true, ...<object>}
 *  If we catch an exception we will ad ok = false to it object like, otherwise we will return {ok:false, message: <exception>}
 * @example
 * okOrCatchSync(() => mightThrowSync('some data'))
 */
export const okOrCatchSync = <T>(
  fn: () => T,
  props?: {
    caughtErrorMessagePrefix?: string
  }
): IsAny<T> extends true ? OK<{}> | Err<Error>
: T extends {ok: boolean} ? T | Err<Error>
: T extends void ? OK<{}> | Err<Error>
: T extends object ? OK<T> | Err<Error>
: OK<{value: T}> | Err<Error> => {
  try {
    const result = fn()
    if (isOk(result) || isErr(result)) return result as any // Already an OK/Err like structure
    return ok(result) as any
  } catch (e: any) {
    const errorMessage = e.message ?? e.toString()
    return err(props?.caughtErrorMessagePrefix ? `${props.caughtErrorMessagePrefix}: ${errorMessage}` : errorMessage)
  }
}

/** Throws errors and return only if OK
 * @example
 * apiMethod().then(okOrThrow) // returns only if ok (narrowed to the ok return type) or throws an error
 */
export const okOrThrow = <T, E extends {message: string}>(p: OK<T> | Err<E>) => {
  if (p.ok) return omit(p, ['ok']) as T
  throw new Error((p as Err<E>).message)
}

/** @deprecated Use okOrCatch instead
 * Like the standard promise try/catch/finally, but allows you to use the result from catch in an expression */
export const doTryAsync = async <Result, ErrorResult, TError extends Error>(
  f: () => Promise<Result>,
  p: {catch?: (e: TError) => ErrorResult; finally?: () => void}
) => {
  try {
    return await f()
  } catch (e) {
    if (!p.catch) throw e
    return p.catch?.(e as TError)
  } finally {
    p.finally?.()
  }
}

/** Like the standard promise try/catch/finally, but allows you to use the result from catch in an expression */
export const doTry = <Result, ErrorResult, TError extends Error>(
  f: () => Result,
  catchFunc?: (e: TError) => ErrorResult
) => {
  try {
    return f()
  } catch (e) {
    return catchFunc?.(e as TError)
  }
}

export const withDisposeAsync = <T extends Object>(o: T, fn: (o: T) => Promise<void>) => {
  if (!(Symbol.dispose in o)) (o as any)[Symbol.dispose] = fn(o)
  return o as any as T & AsyncDisposable
}

export const withDispose = <T extends Object>(o: T, fn: (o: T) => void) => {
  ;(o as any)[Symbol.dispose] = () => fn(o)
  return o as T & Disposable
}

/** Times the execution of a promise and returns both the result and execution time in ms */
export const withTime = async <T>(promise: Promise<T> | (() => Promise<T>)): Promise<[T, Duration]> => {
  const start = performance.now()
  const result = await (typeof promise === 'function' ? promise() : promise)
  const end = performance.now()
  return [result, Duration.fromMillis(end - start)]
}

export const withRetry = async <T extends Res<{}, {}>>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number
    shouldRetry?: (err: T) => boolean
  } = {}
) => {
  const maxRetries = options.maxRetries ?? 3
  const shouldRetry = options.shouldRetry
  let retryCount = 0
  while (retryCount <= maxRetries) {
    const result = (await fn()) as T
    if (result.ok) return result as T
    if (retryCount >= maxRetries) return result as T
    if (shouldRetry && !shouldRetry(result)) return result
    console.log(`Retrying due to invalid result (${retryCount + 1}/${maxRetries})`)
    retryCount++
  }
  throw new Error('withRetry should never get here')
}

export const okOrUndefined = <T extends object, E extends object = {message: string}>(
  value: T | undefined,
  error: E = {message: 'Value is undefined'} as E
): Res<T, E> => (value == null ? err(error) : ok(value))

export const okOrErrPartition = <T extends {ok: boolean}>(
  items: ReadonlyArray<T>
): [Prettify<Omit<Extract<T, {ok: true}>, 'ok'>>[], Prettify<Omit<Extract<T, {ok: false}>, 'ok'>>[]] => {
  const [oks, errs] = partition(items, (x): x is Extract<T, {ok: true}> => x.ok)
  return [oks.map(o => omit(o, ['ok'])), errs.map(e => omit(e, ['ok']))] as any
}

export const sansOk = <T extends {ok: true}>(r: T): Prettify<Omit<T, 'ok'>> => {
  const {ok: _ok, ...rest} = r
  return rest as Prettify<Omit<T, 'ok'>>
}
